import type { TenantConfig } from './TenantContext.ts';

/** Strips trailing `/` in linear time (avoids ReDoS-prone `/\/+$/` regex). */
function stripTrailingSlashes(s: string): string {
  let end = s.length;
  while (end > 0 && s.codePointAt(end - 1) === 47 /* / */) {
    end -= 1;
  }
  return s.slice(0, end);
}

/** Defensive read of `import.meta.env` (tests cover branches without relying on the bundle). */
export function parseEnvMapValue(
  env: Record<string, unknown> | null | undefined,
  key: string
): string | undefined {
  if (env == null) return undefined;
  const v = env[key];
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint' || typeof v === 'symbol') {
    return String(v);
  }
  return undefined;
}

/**
 * `USE_LOCAL_PUNTOS_ENTREGA` injected into the bundle: missing, empty, or `"0"` → no local SQLite
 * (only when the build sets `0`, e.g. `LOCAL_PUNTOS_ENTREGA_DB=0` at compile time).
 */
export function parseUseLocalSqliteFlag(value: string | undefined): boolean {
  if (value === undefined || value.trim() === '') return false;
  return value.trim() !== '0';
}

/** Exposed for tests; same URL normalization as {@link AppConfig} at startup. */
export function normalizeBrowserApiBaseUrl(url: string): string {
  const trimmed = url.trim();
  if (/api:\d+/.test(trimmed) || /:\/\/api:/.test(trimmed)) {
    throw new Error(
      'API_URL cannot use an internal service hostname (api:PORT); the browser cannot resolve it. ' +
        `Use a URL reachable from the client (e.g. http://localhost:…), not ${trimmed}.`,
    );
  }
  return stripTrailingSlashes(trimmed).replace(/\/api$/, '');
}

/** Pure resolver for tests and {@link AppConfig.getAppDisplayTitle}. */
export function resolveAppDisplayTitle(
  adminAppTitle: string | undefined,
  adminTenantName: string | undefined
): string {
  return adminAppTitle?.trim() || adminTenantName?.trim() || 'Admin';
}

/**
 * Build-defined variables (Vite `define`). The browser has no native `import.meta.env`;
 * if a key is not substituted, direct `import.meta.env.KEY` access throws. This avoids that failure.
 */
export function tryReadEnvValue(
  readEnv: () => Record<string, unknown> | undefined,
  key: string
): string | undefined {
  try {
    return parseEnvMapValue(readEnv(), key);
  } catch {
    return undefined;
  }
}

/**
 * Single read path for `import.meta.env` used by {@link readAppConfigEnvKey} and {@link AppConfig}.
 * Object wrapper so Vitest can spy the same code path as `AppConfig` (not only the loose export).
 */
export const appConfigEnvKeyReader = {
  /** Reads a configuration value from the environment. */

  read(key: string): string | undefined {
    // Must stay as literal `import.meta.env`: Vite/Vitest `define` does not replace `(import.meta as …).env`.
    return tryReadEnvValue(() => import.meta.env as Record<string, unknown>, key);
  },
};

/**
 * Resolves one `import.meta.env` key (same logic as {@link AppConfig}).
 * Exported so Vitest/Sonar can instrument and report coverage for this path.
 */
export function readAppConfigEnvKey(key: string): string | undefined {
  return appConfigEnvKeyReader.read(key);
}

/** Joins offline sync POST target from env-style base + path (same rules as bundled `SYNC_*` keys). */
export function combineSyncUploadBaseAndPath(rawBase: string, rawPath: string): string | null {
  const base = stripTrailingSlashes(rawBase.trim());
  const pathRaw = rawPath.trim();
  if (!base || !pathRaw) return null;
  const pathSeg = pathRaw.startsWith('/') ? pathRaw : `/${pathRaw}`;
  try {
    return new URL(`${base}${pathSeg}`).href;
  } catch {
    return null;
  }
}

/** Documentation placeholder hosts from `.env.example`; not valid production sync targets. */
export function isPlaceholderSyncHost(rawBase: string): boolean {
  const trimmed = rawBase.trim();
  if (!trimmed) return false;
  try {
    const href = trimmed.includes('://') ? trimmed : `https://${trimmed}`;
    const host = new URL(href).hostname.toLowerCase();
    return (
      host === 'example.com' ||
      host === 'example.org' ||
      host.endsWith('.example.com') ||
      host.endsWith('.example.org')
    );
  } catch {
    return false;
  }
}

type SyncBaseEnvKey = 'SYNC_UPLOAD_BASE_URL' | 'SYNC_DOWNLOAD_BASE_URL';

/** Explicit sync host from bundle, or {@link AppConfig.getApiBaseUrl} when empty/placeholder. */
export function resolveEffectiveSyncBaseUrl(envKey: SyncBaseEnvKey): string {
  const explicit = appConfigEnvKeyReader.read(envKey)?.trim() ?? '';
  if (explicit && !isPlaceholderSyncHost(explicit)) return stripTrailingSlashes(explicit);
  return AppConfig.getApiBaseUrl();
}

/** Application config from `import.meta.env` (no feature modules). */
export class AppConfig {
  private static apiBaseUrlMemo: string | null = null;

  /** Gets the normalized API base URL from the environment. */
  static getApiBaseUrl(): string {
    if (this.apiBaseUrlMemo === null) {
      const raw = appConfigEnvKeyReader.read('API_URL')?.trim() || 'http://localhost:3000';
      this.apiBaseUrlMemo = normalizeBrowserApiBaseUrl(raw);
    }
    return this.apiBaseUrlMemo;
  }

  /** Session storage key; `ADMIN_TOKEN_STORAGE_KEY` in build. */
  static getTokenKey(): string {
    return appConfigEnvKeyReader.read('ADMIN_TOKEN_STORAGE_KEY')?.trim() || 'admin_access_token';
  }

  /** HTTP prefix for serving `storage` (Nginx, CDN); `ADMIN_FILES_PUBLIC_PREFIX` in build. */
  static getFilesPublicPrefix(): string {
    return appConfigEnvKeyReader.read('ADMIN_FILES_PUBLIC_PREFIX')?.trim() || '/storage';
  }

  /** Gets the IndexedDB database name for local delivery points. */
  static getLocalPuntosEntregaIdbName(): string {
    return appConfigEnvKeyReader.read('LOCAL_PUNTOS_ENTREGA_IDB_NAME')?.trim() || 'bono-puntos-entrega';
  }

  /** Gets the IndexedDB database version for local delivery points. */
  static getLocalPuntosEntregaIdbVersion(): number {
    const raw = appConfigEnvKeyReader.read('LOCAL_PUNTOS_ENTREGA_IDB_VERSION')?.trim();
    if (!raw) return 1;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  /** Gets the IndexedDB store name for local delivery points. */
  static getLocalPuntosEntregaIdbStore(): string {
    return appConfigEnvKeyReader.read('LOCAL_PUNTOS_ENTREGA_IDB_STORE')?.trim() || 'kv';
  }

  /** Gets the IndexedDB key used to store the SQLite database. */
  static getLocalPuntosEntregaIdbSqliteKey(): string {
    return appConfigEnvKeyReader.read('LOCAL_PUNTOS_ENTREGA_IDB_KEY')?.trim() || 'sqlite';
  }

  /** UI title / `document.title`: env `ADMIN_APP_TITLE` → tenant → `Admin`. */
  static getAppDisplayTitle(): string {
    return resolveAppDisplayTitle(
      appConfigEnvKeyReader.read('ADMIN_APP_TITLE'),
      appConfigEnvKeyReader.read('ADMIN_TENANT_NAME'),
    );
  }

  /**
   * Embedded SQLite (sql.js) and login against `usuarios` (no `/auth/login` fetch).
   * Default bundle is `1`; build sets `0` only with `LOCAL_PUNTOS_ENTREGA_DB=0`.
   */
  static useLocalSqliteClient(): boolean {
    return parseUseLocalSqliteFlag(appConfigEnvKeyReader.read('USE_LOCAL_PUNTOS_ENTREGA'));
  }

  /** @internal Vitest only: reset `API_URL` resolution coverage. */
  static resetApiBaseUrlCacheForTests(): void {
    this.apiBaseUrlMemo = null;
  }

  /** Gets the URL for the local SQLite database snapshot. */
  static getLocalSqliteSnapshotUrl(): string {
    return appConfigEnvKeyReader.read('LOCAL_PUNTOS_ENTREGA_DB_URL')?.trim() || '/database/local.db';
  }

  /**
   * Full URL for uploading the offline JSON bundle (`SYNC_UPLOAD_BASE_URL` + `SYNC_UPLOAD_PATH`).
   * Path is required. Base falls back to {@link getApiBaseUrl} when empty or a placeholder host.
   */
  static resolveSyncUploadUrl(): string | null {
    const path = appConfigEnvKeyReader.read('SYNC_UPLOAD_PATH')?.trim() ?? '';
    if (!path) return null;
    return combineSyncUploadBaseAndPath(resolveEffectiveSyncBaseUrl('SYNC_UPLOAD_BASE_URL'), path);
  }

  /**
   * Full URL for downloading master catalogs (`SYNC_DOWNLOAD_BASE_URL` + `SYNC_DOWNLOAD_PATH`).
   * Path is required. Base falls back to {@link getApiBaseUrl} when empty or a placeholder host.
   */
  static resolveSyncDownloadUrl(): string | null {
    const path = appConfigEnvKeyReader.read('SYNC_DOWNLOAD_PATH')?.trim() ?? '';
    if (!path) return null;
    return combineSyncUploadBaseAndPath(resolveEffectiveSyncBaseUrl('SYNC_DOWNLOAD_BASE_URL'), path);
  }

  /**
   * Optional Bearer token: authorizes catalog GET (`SYNC_DOWNLOAD_*`) and offline sync POST (`SYNC_UPLOAD_*`).
   */
  static getSyncDownloadBearerToken(): string | null {
    const token = appConfigEnvKeyReader.read('SYNC_DOWNLOAD_BEARER_TOKEN')?.trim();
    return token ? token : null;
  }

  /**
   * Bearer for catalog download + offline sync POST when those flows bypass {@link HttpClient}:
   * prefers the build-time service token, otherwise the same session JWT used for the API (after login).
   */
  static resolveSyncBearerForOfflineTransport(sessionToken: string | null | undefined): string | null {
    const staticToken = this.getSyncDownloadBearerToken();
    if (staticToken) return staticToken;
    const s = typeof sessionToken === 'string' ? sessionToken.trim() : '';
    return s.length > 0 ? s : null;
  }

  /**
   * Optional URL for pre-sync connectivity HEAD/GET only (`SYNC_CONNECTIVITY_PROBE_BASE_URL` +
   * `SYNC_CONNECTIVITY_PROBE_PATH`). When null, the browser probes the real POST/GET sync URL.
   */
  static resolveSyncConnectivityProbeUrl(): string | null {
    const base = appConfigEnvKeyReader.read('SYNC_CONNECTIVITY_PROBE_BASE_URL')?.trim() ?? '';
    const path = appConfigEnvKeyReader.read('SYNC_CONNECTIVITY_PROBE_PATH')?.trim() ?? '';
    return combineSyncUploadBaseAndPath(base, path);
  }

  /**
   * Whether to send `Idempotency-Key` on the sync POST (`SYNC_UPLOAD_SEND_IDEMPOTENCY_KEY_HEADER` at bundle time).
   * When false, idempotency is carried only by the JSON field `idempotencyKey`, avoiding strict CORS preflight rejects.
   */
  static syncUploadSendIdempotencyKeyHeader(): boolean {
    const raw = appConfigEnvKeyReader.read('SYNC_UPLOAD_SEND_IDEMPOTENCY_KEY_HEADER');
    const n = (typeof raw === 'string' ? raw.trim() : '').toLowerCase();
    if (n === '0' || n === 'false' || n === 'no') return false;
    return true;
  }

  /** Max UTF-8 JSON body size for one offline sync POST (default 8 MB). */
  static syncUploadMaxPayloadBytes(): number {
    const raw = appConfigEnvKeyReader.read('SYNC_UPLOAD_MAX_PAYLOAD_BYTES');
    const n = Number(typeof raw === 'string' ? raw.trim() : raw);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
    return 8_000_000;
  }

  /**
   * Full URL of JSON manifest `{ versionCode, versionName, url }` for in-app APK OTA (Android sideload).
   * Empty when not configured at build time.
   */
  static resolveApkUpdateManifestUrl(): string | null {
    const raw = appConfigEnvKeyReader.read('ANDROID_APK_UPDATE_MANIFEST_URL')?.trim() ?? '';
    if (!raw) return null;
    try {
      return new URL(raw).href;
    } catch {
      return null;
    }
  }

  /** Gets the default tenant configuration including theme colors. */
  static getDefaultTenantConfig(): TenantConfig {
    return {
      id: appConfigEnvKeyReader.read('ADMIN_TENANT_ID')?.trim() || 'default-tenant',
      name: appConfigEnvKeyReader.read('ADMIN_TENANT_NAME')?.trim() || 'Default tenant',
      apiUrl: this.getApiBaseUrl(),
      theme: {
        /* Default: institutional palette (sky blue + flag green); override via ADMIN_THEME_* */
        primaryColor: appConfigEnvKeyReader.read('ADMIN_THEME_PRIMARY')?.trim() || '#1a6fbf',
        secondaryColor: appConfigEnvKeyReader.read('ADMIN_THEME_SECONDARY')?.trim() || '#0d6b47',
      },
    };
  }
}
